/**
 * ScrubInput — input numérico con arrastre 2D
 *
 * Interacción:
 *   Arrastrar ▲▼ (vertical)   → cambia el valor usando el paso activo
 *   Arrastrar ◀▶ (horizontal) → selecciona el paso/precisión en la barra visual
 *   Ambos ejes funcionan simultáneamente
 *
 * Props:
 *   value, onChange, min, max  — control del valor
 *   label                      — etiqueta opcional izquierda
 *   unit                       — sufijo opcional (mm, px…)
 *   stepOptions                — array de pasos disponibles (default: [0.1, 0.5, 1, 5, 10])
 *   defaultStepIndex           — índice del paso por defecto (default: 2 = ×1)
 *   className                  — clases extra para el wrapper
 */
import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const DEFAULT_STEPS = [0.1, 0.5, 1, 5, 10];
const DEFAULT_STEP_IDX = 2;
const ZONE_W = 58;        // px de ancho por cada zona en la barra
const SENSITIVITY = 3;    // px verticales por 1 unidad de paso

// --- Helpers ---

function roundToStep(val, step) {
    const decimals = step < 1 ? String(step).split('.')[1]?.length ?? 1 : 0;
    return parseFloat(val.toFixed(decimals));
}

function clampVal(val, min, max) {
    return Math.min(Math.max(val, min), max);
}

// Colores de acento por zona (de fino a grueso)
const ZONE_COLORS = [
    { bg: 'hsl(210,80%,52%)', text: '#fff' },  // azul  – ×0.1
    { bg: 'hsl(195,70%,46%)', text: '#fff' },  // cían  – ×0.5
    { bg: 'hsl(162,60%,42%)', text: '#fff' },  // verde – ×1
    { bg: 'hsl(32, 90%,50%)', text: '#fff' },  // naranja – ×5
    { bg: 'hsl(4,  82%,52%)', text: '#fff' },  // rojo  – ×10
];

function zoneColor(idx, total) {
    // Interpola entre los colores predefinidos si hay más o menos opciones
    const ratio = idx / Math.max(total - 1, 1);
    const ci = Math.round(ratio * (ZONE_COLORS.length - 1));
    return ZONE_COLORS[Math.min(ci, ZONE_COLORS.length - 1)];
}

// --- Barra de pasos (portal fixed) ---

const StepBar = ({ anchorX, anchorY, stepOptions, activeIdx }) => {
    const totalW = stepOptions.length * ZONE_W;
    const left = clampVal(anchorX - totalW / 2, 8, window.innerWidth - totalW - 8);
    const top  = clampVal(anchorY - 64, 8, window.innerHeight - 80);

    return ReactDOM.createPortal(
        <div
            style={{ position: 'fixed', left, top, zIndex: 99999, pointerEvents: 'none' }}
            className="select-none"
        >
            {/* Hint */}
            <div className="text-center text-[10px] opacity-40 mb-1 tracking-wide">
                ◀ precisión &nbsp;·&nbsp; velocidad ▶
            </div>

            {/* Zonas */}
            <div
                className="flex rounded-xl overflow-hidden shadow-2xl"
                style={{ border: '1px solid rgba(255,255,255,0.12)' }}
            >
                {stepOptions.map((s, idx) => {
                    const active = idx === activeIdx;
                    const { bg, text } = zoneColor(idx, stepOptions.length);
                    return (
                        <div
                            key={s}
                            style={{
                                width: ZONE_W,
                                background: active ? bg : 'var(--fallback-b1,oklch(100% 0 0))',
                                color: active ? text : undefined,
                                opacity: active ? 1 : 0.45,
                                transform: active ? 'scaleY(1.08)' : 'scaleY(1)',
                                transition: 'background 0.12s, opacity 0.12s, transform 0.12s',
                                boxShadow: active ? `0 0 12px ${bg}88` : 'none',
                            }}
                            className="text-center text-xs font-semibold py-2"
                        >
                            ×{s}
                        </div>
                    );
                })}
            </div>

            {/* Aguja indicadora */}
            <div
                style={{
                    position: 'absolute',
                    bottom: -6,
                    left: activeIdx * ZONE_W + ZONE_W / 2 - 5,
                    width: 10,
                    height: 6,
                    transition: 'left 0.1s',
                    clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
                    background: zoneColor(activeIdx, stepOptions.length).bg,
                }}
            />
        </div>,
        document.body
    );
};

// --- Componente principal ---

const ScrubInput = ({
    value,
    onChange,
    min = -Infinity,
    max = Infinity,
    label,
    unit = '',
    stepOptions = DEFAULT_STEPS,
    defaultStepIndex = DEFAULT_STEP_IDX,
    className = '',
}) => {
    const [isDragging, setIsDragging]     = useState(false);
    const [activeStepIdx, setActiveStepIdx] = useState(defaultStepIndex);
    const [anchorPos, setAnchorPos]       = useState({ x: 0, y: 0 });

    const drag = useRef({ startX: 0, startY: 0, startValue: 0 });
    const halfBarW = (stepOptions.length * ZONE_W) / 2;

    const getStepIdx = (deltaX) => {
        const pos = deltaX + halfBarW;
        return Math.max(0, Math.min(stepOptions.length - 1, Math.floor(pos / ZONE_W)));
    };

    const applyMove = useCallback((clientX, clientY) => {
        const { startX, startY, startValue } = drag.current;
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;

        const idx  = getStepIdx(deltaX);
        setActiveStepIdx(idx);

        const step   = stepOptions[idx];
        const rawVal = startValue + (-deltaY / SENSITIVITY) * step;
        onChange(clampVal(roundToStep(rawVal, step), min, max));
    }, [stepOptions, onChange, min, max]);

    const beginDrag = useCallback((clientX, clientY) => {
        drag.current = { startX: clientX, startY: clientY, startValue: Number(value) };
        setAnchorPos({ x: clientX, y: clientY });
        setActiveStepIdx(defaultStepIndex);
        setIsDragging(true);
    }, [value, defaultStepIndex]);

    const handleMouseDown = useCallback((e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        beginDrag(e.clientX, e.clientY);

        const onMove = (ev) => applyMove(ev.clientX, ev.clientY);
        const onUp   = ()   => {
            setIsDragging(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup',   onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
    }, [beginDrag, applyMove]);

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length !== 1) return;
        e.preventDefault();
        const t = e.touches[0];
        beginDrag(t.clientX, t.clientY);

        const onMove = (ev) => {
            const tt = ev.touches[0];
            applyMove(tt.clientX, tt.clientY);
        };
        const onEnd = () => {
            setIsDragging(false);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend',  onEnd);
        };
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend',  onEnd);
    }, [beginDrag, applyMove]);

    const handleDirectInput = (e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) onChange(clampVal(roundToStep(v, stepOptions[defaultStepIndex]), min, max));
    };

    return (
        <div className={`flex items-center gap-1.5 w-full ${className}`}>
            {label && (
                <span className="text-xs text-base-content/60 shrink-0 whitespace-nowrap">
                    {label}
                </span>
            )}

            <div
                className={[
                    'relative flex-1 flex items-center gap-0.5 rounded-md px-1.5 py-1',
                    'border border-base-300 bg-base-100',
                    'cursor-ns-resize select-none transition-all duration-100',
                    'hover:border-primary/40 hover:shadow-sm',
                    isDragging
                        ? 'border-primary/70 bg-primary/5 ring-1 ring-primary/20 shadow-md'
                        : '',
                ].join(' ')}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{ touchAction: 'none', minWidth: 64 }}
                title="Arrastrar ↑↓ para cambiar · ←→ para ajustar precisión"
            >
                {/* Indicador vertical izquierdo */}
                <div className="flex flex-col items-center shrink-0 opacity-25 gap-px">
                    <svg viewBox="0 0 8 5" className="w-2 h-[5px] fill-current">
                        <path d="M4 0L8 5H0z"/>
                    </svg>
                    <svg viewBox="0 0 8 5" className="w-2 h-[5px] fill-current">
                        <path d="M4 5L0 0H8z"/>
                    </svg>
                </div>

                {/* Input numérico */}
                <input
                    type="number"
                    value={value}
                    onChange={handleDirectInput}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    className={[
                        'flex-1 text-center text-sm bg-transparent outline-none border-none',
                        'min-w-0',
                        '[appearance:textfield]',
                        '[&::-webkit-inner-spin-button]:appearance-none',
                        '[&::-webkit-outer-spin-button]:appearance-none',
                    ].join(' ')}
                />

                {/* Unidad */}
                {unit && (
                    <span className="text-[11px] text-base-content/40 shrink-0 pr-0.5">
                        {unit}
                    </span>
                )}
            </div>

            {isDragging && (
                <StepBar
                    anchorX={anchorPos.x}
                    anchorY={anchorPos.y}
                    stepOptions={stepOptions}
                    activeIdx={activeStepIdx}
                />
            )}
        </div>
    );
};

export default ScrubInput;
